import { Component, computed, input } from '@angular/core';
import { FormsModule } from '@angular/forms';

/**
 * Native port of the AngularJS `ftTextField` field-editor directive: a multi-language
 * branch (one `.modal-panel` per language, a native `<input>` bound to the per-language
 * `fieldData[field.name]`) plus a single-datasource branch. `maxLength` renders as the
 * cosmetic `[attr.ng-maxlength]` (a non-blocking validator) to preserve v1 typing behavior;
 * constraints derive from `fieldsOptions`; RTL via `isRtl()` → `[style.direction]`.
 */
@Component({
    selector: 'ft-text-field-ng',
    imports: [FormsModule],
    templateUrl: './ft-text-field.component.html'
})
export class FtTextFieldComponent {
    /** The object/array whose text fields the inputs edit. Array → multi-language branch. */
    readonly datasource = input<any>();
    /** A field name, or `{name,label}`, or a list of either — normalised below. */
    readonly fields = input<any>();
    /**
     * The v1 `attrs`-derived constraints, passed as a raw object (e.g. `{'ng-maxlength': 50}`).
     * Keys: `type`, `required`, `ng-minlength`, `ng-maxlength`, `pattern`.
     */
    readonly fieldsOptions = input<any>();

    /** RTL language codes — v1 `rtlLanguages` constant (languages.js). */
    private readonly rtlLanguages = ['ar', 'he'];

    // v1 textField.js link: isMultiLanguage = angular.isArray(datasource) — evaluated ONCE
    // at link time. A modal opened before the host's async data lands (datasource undefined)
    // stays on the single-input branch FOREVER, even after the live datasource becomes the
    // languages array (only the bound VALUE updates). Freeze the branch at first read to
    // reproduce that (the cert/reminder-email editors hit this when a field is opened fast).
    private frozenIsMultiLanguage: boolean | undefined;
    protected isMultiLanguage(): boolean {
        if (this.frozenIsMultiLanguage === undefined) this.frozenIsMultiLanguage = Array.isArray(this.datasource());
        return this.frozenIsMultiLanguage;
    }
    protected readonly languages = computed<any[]>(() => (this.isMultiLanguage() && Array.isArray(this.datasource()) ? this.datasource() : []));
    /** Single-branch model target — v1's `ng-model="datasource[field.name]"` silently no-oped
     * (rendered empty) on an undefined/array datasource; an unguarded index access would throw. */
    protected readonly singleSource = computed<any>(() => {
        const d = this.datasource();
        return d && !Array.isArray(d) ? d : {};
    });
    /** Stable per-language rows: { language, fieldData } (v1 `ng-init` fieldData), for [(ngModel)]. */
    protected readonly rows = computed<Array<{ language: any; fieldData: any }>>(() =>
        // v1 `ng-init="fieldData = language.data ? language.data : language"` — precompute stable refs.
        this.languages().map((l: any) => ({ language: l, fieldData: l && l.data ? l.data : l }))
    );

    protected readonly normalizedFields = computed<Array<{ name: string; label: string | null; placeholder?: string }>>(() => {
        // v1 `adjustFields`: coerce string entries to {name,label:null}.
        const fields = this.fields();
        const raw = Array.isArray(fields) ? fields : fields == null ? [] : [fields];
        return raw.map((f: any) => (typeof f === 'string' ? { name: f, label: null } : f));
    });

    // v1 read attrs.type / attrs.required / attrs.ngMinlength / attrs.ngMaxlength / attrs.pattern;
    // the native modal passes the raw fieldsOptions object, so derive from its keys (same defaults).
    // v1 link-fn scope defaults.
    protected readonly type = computed<string>(() => (this.fieldsOptions() || {})['type'] || 'text');
    protected readonly required = computed<boolean>(() => {
        const o = this.fieldsOptions() || {};
        return (o['required'] != null && o['required'] !== false && o['required'] !== 'false') || false;
    });
    protected readonly minLength = computed<number>(() => {
        const o = this.fieldsOptions() || {};
        return o['ng-minlength'] != null ? +o['ng-minlength'] : 0;
    });
    protected readonly maxLength = computed<number>(() => {
        const o = this.fieldsOptions() || {};
        return o['ng-maxlength'] != null ? +o['ng-maxlength'] : 1024;
    });
    protected readonly pattern = computed<string>(() => (this.fieldsOptions() || {})['pattern'] || '');

    /**
     * v1 ftDirection (direction.js): isRtl = isDefined(language.isRtl) ? parse.bool(language.isRtl)
     *   : rtlLanguages.includes(language.code).
     */
    protected isRtl(language: any): boolean {
        if (!language) return false;
        if (language.isRtl !== undefined && language.isRtl !== null) {
            return this.parseBool(language.isRtl);
        }
        return this.rtlLanguages.indexOf(language.code) !== -1;
    }

    /** v1 `parse.bool` — truthy except the string 'false'/'0'/'' and falsy primitives. */
    private parseBool(value: any): boolean {
        if (typeof value === 'string') {
            const v = value.toLowerCase();
            return v !== '' && v !== 'false' && v !== '0';
        }
        return !!value;
    }
}
